/**
 * PATH: apps/qa/src/pages/AuditLogPage.tsx
 * PURPOSE: View immutable audit trail of all QA actions
 * 
 * WHY: Compliance requires complete traceability. This page provides:
 * - Full audit history for any inspection
 * - Filtering by action type, user, date range
 * - Hash verification for integrity
 * - Export capability for auditors
 * 
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Fetch Audit │───▶│ Display      │───▶│ Filter &    │
 *   │ Events      │    │ Timeline     │    │ Export      │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 * 
 * DEPENDENCIES:
 *   - @quotech/api for API client
 *   - @tanstack/react-query for data fetching
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import api from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import {
  History,
  Search,
  Filter,
  ChevronDown,
  Download,
  RefreshCw,
  User,
  Clock,
  Shield,
  CheckCircle,
  Plus,
  Edit,
  Trash2,
  Eye,
  FileText,
  Image,
  AlertTriangle,
  Send,
  Hash,
  Copy,
  Check,
  X,
} from 'lucide-react'

// ============================================================================
// Types (aligned with backend AuditEventSerializer)
// ============================================================================

interface AuditEvent {
  id: string
  organisation: string
  actor_user: string | null
  actor_name: string | null
  actor_email: string | null
  actor_role: string | null
  source_app: string | null
  entity_type: string
  entity_id: string
  action: string
  request_id: string | null
  idempotency_key: string | null
  correlation_id: string | null
  before_hash: string | null
  after_hash: string | null
  diff: Record<string, unknown> | null
  reason: string | null
  created_at: string
}

interface AuditEventResponse {
  results: AuditEvent[]
  count: number
}

// ============================================================================
// API
// ============================================================================

async function fetchAuditEvents(params: {
  entity_type?: string
  entity_id?: string
  page?: number
  page_size?: number
}): Promise<AuditEventResponse> {
  const response = await api().get('/qa/audit-events/', { params })
  return response.data
}

// ============================================================================
// Component
// ============================================================================

const entityTypes = [
  { value: '', label: 'All Entities' },
  { value: 'inspection', label: 'Inspections' },
  { value: 'floor', label: 'Floors' },
  { value: 'area', label: 'Areas' },
  { value: 'item_instance', label: 'Checklist Items' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'hold_point', label: 'Hold Points' },
  { value: 'ncr', label: 'NCRs' },
  { value: 'template', label: 'Templates' },
  { value: 'report', label: 'Reports' },
]

export default function AuditLogPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [copiedHash, setCopiedHash] = useState<string | null>(null)

  // Wait for org context before fetching
  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-audit-events', { entityTypeFilter, page, organisationId }],
    queryFn: () => fetchAuditEvents({
      entity_type: entityTypeFilter || undefined,
      page,
      page_size: 50,
    }),
    enabled: isAuthReady,
  })
  
  const showLoading = isLoading || !isAuthReady

  const filteredEvents = data?.results?.filter((event) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      event.entity_type?.toLowerCase().includes(query) ||
      event.action?.toLowerCase().includes(query) ||
      event.actor_email?.toLowerCase().includes(query) ||
      event.actor_name?.toLowerCase().includes(query) ||
      event.reason?.toLowerCase().includes(query)
    )
  })

  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash)
    setCopiedHash(hash)
    setTimeout(() => setCopiedHash(null), 2000)
  }

  const getActionIcon = (action: string) => {
    if (action.includes('create')) return Plus
    if (action.includes('update')) return Edit
    if (action.includes('delete')) return Trash2
    if (action.includes('submit')) return Send
    if (action.includes('evidence')) return Image
    if (action.includes('signature')) return Edit
    if (action.includes('report')) return FileText
    if (action.includes('ncr')) return AlertTriangle
    if (action.includes('view')) return Eye
    return History
  }

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
    if (action.includes('delete')) return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
    if (action.includes('submit')) return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    if (action.includes('ncr')) return 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
  }

  const getSourceBadge = (sourceApp: string | null) => {
    const styles: Record<string, string> = {
      qa: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      jobs: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      crm: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
      system: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
    }
    return styles[sourceApp || 'system'] || styles.system
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 text-white">
              <History className="w-5 h-5" />
            </div>
            Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">
            Complete, immutable record of all QA actions
          </p>
        </div>
        <button 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-foreground font-medium hover:bg-accent transition-colors"
        >
          <Download className="w-4 h-4" />
          Export Log
        </button>
      </div>

      {/* Integrity Banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800 p-4 flex items-center gap-4">
        <div className="p-3 rounded-xl bg-emerald-500 text-white">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <p className="font-semibold text-emerald-800 dark:text-emerald-300">
            Cryptographic Integrity Verified
          </p>
          <p className="text-sm text-emerald-700 dark:text-emerald-400">
            All events are hash-chained and tamper-evident. Click any event to view its hash.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle className="w-4 h-4" />
          Chain Valid
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Entity Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value)
                setPage(1)
              }}
              className="appearance-none pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer min-w-[180px]"
            >
              {entityTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-2.5 rounded-xl border border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Events Timeline */}
      <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
        {showLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-xl bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <p className="font-medium text-foreground">Error loading audit log</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredEvents?.length ? (
          <div className="divide-y divide-border">
            {filteredEvents.map((event) => {
              const Icon = getActionIcon(event.action)
              const iconColor = getActionColor(event.action)
              
              return (
                <div 
                  key={event.id} 
                  className="p-4 hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={cn('p-2.5 rounded-xl flex-shrink-0', iconColor)}>
                      <Icon className="w-4 h-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium text-foreground">
                            {event.reason || event.action.replace(/_/g, ' ').replace(/\./g, ' › ')}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {event.source_app && (
                              <span className={cn(
                                'px-2 py-0.5 rounded-full text-xs font-medium',
                                getSourceBadge(event.source_app)
                              )}>
                                {event.source_app}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {event.entity_type} • {event.entity_id.slice(0, 8)}...
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(event.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* User & Meta */}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        {event.actor_email && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {event.actor_name || event.actor_email}
                          </span>
                        )}
                        {event.actor_role && (
                          <span className="hidden sm:block">
                            {event.actor_role}
                          </span>
                        )}
                      </div>

                      {/* Hash (expandable) */}
                      {event.after_hash && (
                        <div className="mt-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Hash className="w-3 h-3 text-muted-foreground" />
                          <code className="text-xs text-muted-foreground font-mono">
                            {event.after_hash.slice(0, 16)}...
                          </code>
                          <button
                            onClick={() => handleCopyHash(event.after_hash!)}
                            className="p-1 rounded hover:bg-muted transition-colors"
                            title="Copy full hash"
                          >
                            {copiedHash === event.after_hash ? (
                              <Check className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-muted-foreground" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
              <History className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="font-medium text-foreground">No audit events found</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              {searchQuery || entityTypeFilter
                ? 'Try adjusting your search or filters'
                : 'Audit events will appear here as actions are taken'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.count > 50 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{((page - 1) * 50) + 1}</span> - <span className="font-medium text-foreground">{Math.min(page * 50, data.count)}</span> of <span className="font-medium text-foreground">{data.count}</span> events
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * 50 >= data.count}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
